import * as Yup from "yup";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  MenuItem,
  Stack,
  Typography
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import SyncLoader from "react-spinners/SyncLoader";
import Page from "../../../components/Page";
import { FormProvider, RHFTextField } from "../../../components/hook-form";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getFileUrl } from "../../../utils/firebaseUtils";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Title required"),
  is_active: Yup.string().required("Status required")
});

const defaultValues = {
  name: "",
  is_active: "true"
};

export default function EditSubject() {
  const { subjectId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [imageUrl, setImageUrl] = useState("");
  const [imageLoading, setImageLoading] = useState(false);
  const [focusThree, setFocusedThree] = useState(false);
  const [pdf, setPdf] = useState(false);

  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues
  });

  const { handleSubmit, reset } = methods;

  useEffect(() => {
    const fetchSubject = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${process.env.REACT_APP_LIVE_URL_NEW}/admin/classroom-subjects/${subjectId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
            }
          }
        );

        const result = await response.json();
        if (result?.data) {
          reset({
            name: result.data.name ?? "",
            is_active: result.data.is_active ?? "true"
          });
          setImageUrl(result.data.image_url || "");
        } else {
          toast.error(result?.message || "Unable to load subject details");
        }
      } catch (error) {
        toast.error("Unable to load subject details");
      } finally {
        setFetching(false);
      }
    };

    fetchSubject();
  }, [subjectId, reset]);

  const handleFileChange = async (event) => {
    if (!event.target?.files?.length) {
      return;
    }
    try {
      setImageLoading(true);
      const url = await getFileUrl(event.target.files[0]);
      setImageUrl(url);
    } catch (error) {
      toast.error("Failed to upload image. Please try again.");
    } finally {
      setImageLoading(false);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_LIVE_URL_NEW}/admin/classroom-subjects/${subjectId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            ...data,
            image_url: imageUrl || ""
          })
        }
      );
      const result = await response.json();
      if (result.response_code === 200) {
        toast.success(result.message || "Subject updated successfully");
        setTimeout(() => navigate("/dashboard/subject"), 1000);
      } else {
        toast.error(result.message || "Failed to update subject");
      }
    } catch (error) {
      toast.error("Failed to update subject");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <Page name="Edit Subject">
        <Stack
          justifyContent="center"
          alignItems="center"
          sx={{ height: "60vh" }}
        >
          <SyncLoader color="#8080ff" loading size={20} />
        </Stack>
        <ToastContainer
          position="bottom-center"
          autoClose={1000}
          hideProgressBar
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </Page>
    );
  }

  return (
    <Page name="Edit Subject">
      <Stack spacing={3} px={5} py={4} maxWidth="800px">
        <Typography variant="h3">Edit Subject</Typography>
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={3} sx={{ width: "70%" }}>
            <RHFTextField name="name" label="Title" />
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Current Subject Image
              </Typography>
              {imageUrl ? (
                <Box
                  component="img"
                  src={imageUrl}
                  alt="Subject"
                  sx={{ width: 200, height: 120, objectFit: "cover", borderRadius: 1 }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No image available
                </Typography>
              )}
            </Box>
            <RHFTextField
              name="image"
              onChange={handleFileChange}
              id="subject-image"
              label="Subject Image (Optional)"
              helperText={imageLoading ? "Uploading..." : "Image Only"}
              onFocus={() => setFocusedThree(true)}
              onBlur={() => setFocusedThree(false)}
              InputProps={{
                classes: {
                  input: "CustomTextField"
                }
              }}
              onClick={() => setPdf(true)}
              inputProps={{ accept: "image/*" }}
              type={pdf || focusThree ? "file" : "text"}
            />
            <RHFTextField name="is_active" label="Status" select>
              <MenuItem value="true">Publish</MenuItem>
              <MenuItem value="false">Draft</MenuItem>
            </RHFTextField>

            <LoadingButton
              fullWidth
              size="large"
              type="submit"
              variant="contained"
              loading={loading}
              disabled={imageLoading}
              sx={{
                bgcolor: "#e6e6ff",
                color: "black",
                mt: 2,
                "&:hover": { background: "#8080ff" }
              }}
            >
              Update
            </LoadingButton>
          </Stack>
        </FormProvider>
        <ToastContainer
          position="bottom-center"
          autoClose={1000}
          hideProgressBar
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </Stack>
    </Page>
  );
}
